import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Lock, Loader2 } from "lucide-react";

function PinField({ label, testid, inputRef }) {
  return (
    <div>
      <Label className="text-slate-700 font-semibold text-sm">{label}</Label>
      <Input
        data-testid={testid}
        ref={inputRef}
        type="password"
        inputMode="numeric"
        maxLength={4}
        pattern="[0-9]*"
        placeholder="••••"
        onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, "").slice(0, 4))}
        className="mt-1.5 h-12 text-center text-2xl tracking-[0.5em] font-mono-data border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20"
      />
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { pinEnabled, setPin, disablePin, lock } = useAuth();
  const [busy, setBusy] = useState(false);

  const newRef = useRef(null);
  const confirmRef = useRef(null);
  const currentRef = useRef(null);
  const changeCurrentRef = useRef(null);
  const changeNewRef = useRef(null);
  const changeConfirmRef = useRef(null);
  const disableRef = useRef(null);

  const enablePin = async () => {
    const pin = newRef.current?.value || "";
    const confirm = confirmRef.current?.value || "";
    if (pin.length !== 4) return toast.error("PIN must be 4 digits");
    if (pin !== confirm) return toast.error("PINs do not match");
    setBusy(true);
    const res = await setPin(pin);
    setBusy(false);
    if (res.ok) {
      toast.success("PIN lock enabled");
      newRef.current.value = "";
      confirmRef.current.value = "";
    } else toast.error(res.error);
  };

  const changePin = async () => {
    const current = changeCurrentRef.current?.value || "";
    const pin = changeNewRef.current?.value || "";
    const confirm = changeConfirmRef.current?.value || "";
    if (pin.length !== 4) return toast.error("New PIN must be 4 digits");
    if (pin !== confirm) return toast.error("New PINs do not match");
    setBusy(true);
    const res = await setPin(pin, current);
    setBusy(false);
    if (res.ok) {
      toast.success("PIN updated");
      [changeCurrentRef, changeNewRef, changeConfirmRef].forEach((r) => (r.current.value = ""));
    } else toast.error(res.error);
  };

  const turnOff = async () => {
    const current = disableRef.current?.value || "";
    if (current.length !== 4) return toast.error("Enter your current PIN");
    setBusy(true);
    const res = await disablePin(current);
    setBusy(false);
    if (res.ok) {
      toast.success("PIN lock disabled");
      disableRef.current.value = "";
    } else toast.error(res.error);
  };

  return (
    <div className="min-h-screen bg-[#F8F6F0]">
      <TopBar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-24">
        <button
          data-testid="back-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1B3B2B] mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <h1 className="font-headline text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-1">Settings</h1>
        <p className="text-sm text-slate-500 mb-6">Manage privacy for this device.</p>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-fade-up">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-lg bg-[#E8F0EC] flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-[#1B3B2B]" />
              </span>
              <div>
                <p className="font-headline font-semibold text-slate-800">PIN Lock</p>
                <p className="text-xs text-slate-500">
                  {pinEnabled ? "On — a 4-digit PIN is required to open the app" : "Off — the app opens instantly"}
                </p>
              </div>
            </div>
            <span
              data-testid="pin-status-badge"
              className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                pinEnabled ? "bg-[#D1FAE5] text-[#059669]" : "bg-slate-100 text-slate-500"
              }`}
            >
              {pinEnabled ? "On" : "Off"}
            </span>
          </div>

          <div className="p-5 space-y-5">
            {!pinEnabled ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Set a 4-digit PIN so the app stays private on a shared computer or phone.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <PinField label="New PIN" testid="new-pin-input" inputRef={newRef} />
                  <PinField label="Confirm PIN" testid="confirm-pin-input" inputRef={confirmRef} />
                </div>
                <Button
                  data-testid="enable-pin-button"
                  onClick={enablePin}
                  disabled={busy}
                  className="w-full h-12 bg-[#1B3B2B] hover:bg-[#254F3A] text-white font-semibold rounded-lg"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Enable PIN Lock</>}
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-slate-700">Change PIN</p>
                  <PinField label="Current PIN" testid="change-current-pin-input" inputRef={changeCurrentRef} />
                  <div className="grid grid-cols-2 gap-4">
                    <PinField label="New PIN" testid="change-new-pin-input" inputRef={changeNewRef} />
                    <PinField label="Confirm New PIN" testid="change-confirm-pin-input" inputRef={changeConfirmRef} />
                  </div>
                  <Button
                    data-testid="change-pin-button"
                    onClick={changePin}
                    disabled={busy}
                    variant="outline"
                    className="w-full h-11 border-slate-300 text-slate-700 font-semibold rounded-lg"
                  >
                    Update PIN
                  </Button>
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-3">
                  <p className="text-sm font-semibold text-slate-700">Turn off PIN Lock</p>
                  <PinField label="Enter current PIN to disable" testid="disable-pin-input" inputRef={disableRef} />
                  <Button
                    data-testid="disable-pin-button"
                    onClick={turnOff}
                    disabled={busy}
                    variant="outline"
                    className="w-full h-11 border-[#DC2626]/30 text-[#DC2626] hover:bg-[#FEE2E2] font-semibold rounded-lg"
                  >
                    Disable PIN Lock
                  </Button>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <Button
                    data-testid="lock-now-button"
                    onClick={() => { lock(); navigate("/"); }}
                    className="w-full h-11 bg-[#152E22] hover:bg-[#0f2318] text-white font-semibold rounded-lg"
                  >
                    <Lock className="h-4 w-4 mr-2" /> Lock Now
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
